// media-library.component.ts
// ============================================================================
// MEDIA LIBRARY COMPONENT (Main Container)
// ============================================================================
// Purpose: Main component that orchestrates the media library functionality
// Handles file selection, upload state management, and communication between
// child components (upload zones, change zones, and modal)
// ============================================================================

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  MediaLibraryCategoryEnum,
  MediaLibraryFileModel,
  MediaLibraryTypeModel,
  MediaLibraryUploadType,
} from 'src/app/models/sales-funnel';
import { ApiService } from '../../../shared/services/lsg.api.service';
import { UserDashboardService } from '../../../shared/services/user-dashboard.service';
import { ToastrService } from 'ngx-toastr';
import { UploadMetaData } from './utils';

@Component({
  selector: 'app-media-library',
  templateUrl: './media-library.component.html',
  styleUrls: ['./media-library.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaLibraryComponent implements OnInit, OnChanges {
  // ============================================================================
  // PUBLIC PROPERTIES - Modal & State Management
  // ============================================================================

  /** Controls the visibility of the upload modal */
  isUploadModalOpen: boolean = false;

  /** Reference to media library category enumeration for template usage */
  mediaLibraryCategoryEnum = MediaLibraryCategoryEnum;

  /** Indicates whether initial data has been loaded */
  loaded = false;

  // ============================================================================
  // PUBLIC PROPERTIES - File Specifications & Filters
  // ============================================================================

  /** Image dimension restrictions for upload validation */
  restrict: {
    width?: number;
    height?: number;
  };

  /** Array of allowed file type extensions for filtering */
  filterFilesBy: string[] = [];

  // ============================================================================
  // INPUT PROPERTIES - Component Configuration
  // ============================================================================

  /** Determines if file selection is mandatory */
  @Input() required = false;

  /** Controls whether to display borders around media items */
  @Input() enableBorders = false;

  /** Allows multiple file selection when true */
  @Input() multiFiles = true;

  /** Enables view-only mode (no editing capabilities) */
  @Input() view = false;

  /**
   * Specifies the type of files that can be uploaded
   * Options: any, content, media, video, image, audio
   */
  @Input() uploadType: MediaLibraryUploadType | string =
    MediaLibraryUploadType.Media;

  /**
   * Image specifications for validation and display
   * Includes width, height, and aspect ratio constraints
   */
  @Input() imgSpecs: {
    width?: number;
    height?: number;
    ratio?: number;
  } = {
      width: null,
      height: null,
      ratio: null,
    };

  /** Category classification for uploaded media (e.g., thumbnail, banner, etc.) */
  @Input() uploadCategory: MediaLibraryCategoryEnum;

  /** Array of currently selected file objects */
  @Input() selectedFiles: MediaLibraryFileModel[] = [];

  /** Temporary storage for selected files during modal operations */
  @Input() selectedFilesTemp: MediaLibraryFileModel[] = [];

  /** Temporary storage for selected file IDs during modal operations */
  @Input() selectedFilesTempIds: number[] = [];

  /** Array of selected file IDs for tracking and API operations */
  @Input() selectedFilesIds: number[] = [];

  /** Custom label for the upload field (displayed in UI) */
  @Input() uploadFieldLabel: string;

  @Input() isAvatar: boolean = false;

  /** Controls visibility of the field label */
  @Input() hideFieldLabel: boolean = false;

  /**
   * Maximum allowed file size in bytes for uploads
   * Defaults to 4MB (4194304 bytes) if not specified
   */
  @Input() maxFileSize: number = 10485760;

  /** Indicates if the field is invalid for styling purposes */
  @Input() isInvalid: boolean = false;

  /** Indicates if the field is valid for styling purposes */
  @Input() isValid: boolean = false;

  /** Validation error message to display */
  @Input() invalidFeedback: string = '';

  // ============================================================================
  // OUTPUT PROPERTIES - Event Emitters
  // ============================================================================

  /** Emits when selected files array changes */
  @Output() selectedFilesChange: EventEmitter<MediaLibraryFileModel[]> =
    new EventEmitter<MediaLibraryFileModel[]>();

  /** Emits when selected file IDs array changes */
  @Output() selectedFilesIdsChange: EventEmitter<number[]> = new EventEmitter<
    number[]
  >();

  // ============================================================================
  // PUBLIC PROPERTIES - Data Storage
  // ============================================================================

  /** 2D array for paginated media storage */
  mediaStorage: MediaLibraryFileModel[][] = [];

  /** Flat array of all media library files */
  mediaLibraryFiles: MediaLibraryFileModel[] = [];

  /** Available media types configuration */
  mediaLibraryTypes: MediaLibraryTypeModel[] = [];

  /** Reference to active toast notification for uploads */
  uploadToast: any;

  /** Dynamic text for upload button/area based on upload type */
  uploadText: string = 'File Upload';

  uploadExtensions: Record<'image' | 'video' | 'file', string[]> = {
    image: ['JPG', 'PNG', 'WEBP', 'JPEG', 'SVG'],
    video: ['MP4', 'MOV', 'AVI', 'MKV'],
    file: ['PDF', 'DOC', 'DOCX', 'TXT', 'ZIP', 'BIN', 'PPT', 'PPTX'],
  };

  uploadMetaData: UploadMetaData;
  // ============================================================================
  // CONSTRUCTOR
  // ============================================================================

  constructor(
    private formBuilder: UntypedFormBuilder,
    private modalService: NgbModal,
    private route: Router,
    public userDashboardService: UserDashboardService,
    private api: ApiService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) {
    // Bind error handler to maintain correct 'this' context
    this.addMediaFileErrorHandler = this.addMediaFileErrorHandler.bind(this);
  }

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  ngOnInit(): void {
    // Initial generation of upload text (can be overridden by ngOnChanges if inputs change)
    this.generateUploadText();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Defensive normalization: an @Input() default value (e.g. `= []`) only
    // applies before Angular ever sets a binding. If the parent binds a
    // literal `null`/`undefined` (e.g. `[selectedFiles]="form.value.avatar"`
    // when that control's value is null), it overwrites the default and
    // every internal `.filter()`/`.some()` call on these arrays will throw.
    // Normalize on every change so this component is safe regardless of
    // what the parent passes in.
    if (changes['selectedFiles'] && this.selectedFiles == null) {
      this.selectedFiles = [];
    }
    if (changes['selectedFilesIds'] && this.selectedFilesIds == null) {
      this.selectedFilesIds = [];
    }
    if (changes['selectedFilesTemp'] && this.selectedFilesTemp == null) {
      this.selectedFilesTemp = [];
    }
    if (changes['selectedFilesTempIds'] && this.selectedFilesTempIds == null) {
      this.selectedFilesTempIds = [];
    }

    if (changes['uploadType'] || changes['uploadFieldLabel']) {
      this.generateUploadText();
      this.uploadMetaData = this.getUploadMetaDataObject();
    }
  }

  // ============================================================================
  // PUBLIC METHODS - Modal Management
  // ============================================================================

  /**
   * Opens the media library modal for file selection
   * Sets the modal open state which triggers the modal component
   */
  openMediaLibrary(): void {
    this.isUploadModalOpen = true;
  }

  // ============================================================================
  // PUBLIC METHODS - Selection Management
  // ============================================================================

  onRemoveFile(file: MediaLibraryFileModel): void {
    // Guard against selectedFiles/selectedFilesIds ever being null/undefined
    // (can happen if the parent binds a control whose value is null, e.g.
    // an avatar-less contact) — this is what was throwing before and
    // silently killing the emit() calls below it.
    const currentFiles = this.selectedFiles ?? [];
    const currentIds = this.selectedFilesIds ?? [];

    this.selectedFiles = currentFiles.filter((f) => f.id !== file.id);
    this.selectedFilesIds = currentIds.filter((id) => id !== file.id);

    // Always emit — including the empty-array case — so the parent form
    // control is explicitly updated to reflect the removal instead of
    // silently retaining its previous (stale) value.
    this.selectedFilesChange.emit(this.selectedFiles);
    this.selectedFilesIdsChange.emit(this.selectedFilesIds);
    this.cdr.markForCheck();
  }

  // ============================================================================
  // PUBLIC METHODS - UI Text Generation
  // ============================================================================

  /**
   * Generates appropriate upload text based on upload type and custom label
   * Falls back to default text if no custom label is provided
   */
  generateUploadText(): void {
    // Mapping of upload types to their display text
    const uploadTypeToText: any = {
      video: 'Video Upload',
      image: 'Image Upload',
      audio: 'Audio Upload',
      any: 'File Upload',
      media: 'Media Upload',
      content: 'Content Upload',
    };

    // Use custom label if provided, otherwise use type-based text
    if (!!this.uploadFieldLabel) {
      this.uploadText = `${this.uploadFieldLabel} Upload`;
    } else {
      this.uploadText = uploadTypeToText[this.uploadType] || 'File Upload';
    }
  }

  // ============================================================================
  // PUBLIC METHODS - Error Handling
  // ============================================================================

  /**
   * Handles errors during file upload operations
   * Dismisses any active upload toast and displays error notification
   * @param error - The error object from the upload operation
   */
  addMediaFileErrorHandler(error: any): void {
    // Remove any existing upload progress toast
    this.dismissUploadToast();

    // Display error notification to user
    this.toastr.error(
      'Cannot upload this file. Please try again.',
      'Upload Failed',
      {
        closeButton: true,
        progressBar: true,
        positionClass: 'toast-bottom-right',
        timeOut: 5000,
      }
    );
  }

  // ============================================================================
  // PUBLIC METHODS - Toast Notifications
  // ============================================================================

  /**
   * Displays a toast notification indicating upload progress
   * Shows the number of files being uploaded
   * @param count - Number of files being uploaded
   */
  uploadingWithCountToast(count: number): void {
    this.uploadToast = this.toastr.info(
      `Uploading ${count} ${count > 1 ? 'Files' : 'File'}...`,
      'Processing',
      {
        closeButton: false,
        positionClass: 'toast-bottom-right',
        toastClass: 'ngx-toastr bg-primary text-white shadow-lg rounded-lg',
        disableTimeOut: true,
      }
    );
  }

  /**
   * Dismisses the currently active upload toast notification
   */
  dismissUploadToast(): void {
    if (this.uploadToast?.toastId) {
      this.toastr.remove(this.uploadToast.toastId);
    }
  }

  getUploadMetaDataObject(): UploadMetaData {
    return {
      uploadExtType: this.uploadExtType,
      uploadExtensionsByType: this.uploadExtensionsByType,
      uploadTypeLabel: this.uploadTypeLabel,
      visibleExts: this.visibleExts,
      hiddenExts: this.hiddenExts,
      hasHidden: this.hasHidden,
    };
  }

  get uploadExtType(): 'image' | 'video' | 'media' | 'file' {
    const isMedia =
      this.uploadType == 'image' ||
      this.uploadType == 'video' ||
      this.uploadType == 'media';
    if (isMedia) {
      return this.uploadType as 'image' | 'video' | 'media';
    } else {
      return 'file';
    }
  }

  get uploadExtensionsByType() {
    if (this.uploadExtType == 'media') {
      return [...this.uploadExtensions.image, ...this.uploadExtensions.video];
    } else {
      return this.uploadExtensions[this.uploadExtType];
    }
  }

  get uploadTypeLabel() {
    if (this.uploadType == 'image') {
      return 'Image';
    } else if (this.uploadType == 'video') {
      return 'Video';
    } else if (this.uploadType == 'media') {
      return 'Media';
    } else {
      return 'File';
    }
  }

  get visibleExts() {
    return this.uploadExtensionsByType.slice(0, 3);
  }

  get hiddenExts() {
    return this.uploadExtensionsByType.length > 3
      ? this.uploadExtensionsByType.slice(3)
      : [];
  }

  get hasHidden() {
    return this.hiddenExts.length > 0;
  }
}